import { Link, usePage } from "@inertiajs/react";
import {
  BookOpen,
  CalendarDays,
  ChevronsUpDown,
  Cog,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  MessagesSquare,
  UserX,
  Users,
  UsersRound,
} from "lucide-react";

import type { SharedProps } from "@/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { routes } from "@/lib/routes";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [{ label: "Dashboard", icon: LayoutDashboard, href: routes.dashboard }],
  },
  {
    label: "Catalog",
    items: [
      { label: "Tutors", icon: GraduationCap, href: routes.tutors.index },
      { label: "Parents", icon: UsersRound, href: routes.parents.index },
      { label: "Unassigned", icon: UserX, href: routes.users.index },
      { label: "Subjects", icon: BookOpen, href: routes.subjects.index },
    ],
  },
  {
    label: "Activity",
    items: [
      { label: "Bookings", icon: CalendarDays, href: routes.bookings.index },
      { label: "Conversations", icon: MessagesSquare, href: routes.conversations.index },
    ],
  },
  {
    label: "Admin",
    items: [
      { label: "Admins", icon: Users, href: routes.admins.index },
      { label: "Jobs", icon: Cog, href: routes.jobs.index },
    ],
  },
];

export default function AppSidebar() {
  const {
    props: { current_user },
  } = usePage<SharedProps>();
  const { isMobile, setOpenMobile } = useSidebar();
  const closeOnMobile = () => isMobile && setOpenMobile(false);

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              render={<Link href={routes.dashboard} onClick={closeOnMobile} />}
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-md bg-sidebar-primary text-sm font-bold text-sidebar-primary-foreground">
                KP
              </div>
              <span className="truncate font-semibold">Kinderplatz</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {NAV_GROUPS.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map(({ label, icon: Icon, href }) => (
                  <SidebarMenuItem key={label}>
                    <SidebarMenuButton
                      tooltip={label}
                      render={<Link href={href} onClick={closeOnMobile} />}
                    >
                      <Icon />
                      <span>{label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <SidebarMenuButton className="data-popup-open:bg-sidebar-accent data-popup-open:text-sidebar-accent-foreground" />
                }
              >
                <span className="truncate">{current_user.email}</span>
                <ChevronsUpDown className="ml-auto" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-(--anchor-width) min-w-56"
                side="top"
                align="start"
                sideOffset={4}
              >
                <DropdownMenuItem
                  render={<Link href={routes.sessions.destroy} method="delete" as="button" />}
                  className="w-full cursor-pointer"
                >
                  <LogOut />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
